import { DesktopTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import {
  Dialog,
  DialogContent,
  CardContent,
  Grid,
  Button,
  TextField,
  IconButton,
  MaskedInput,
  CircularProgress,
  Autocomplete,
  DatePicker,
} from "../../../components";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { formatDate, responseErros } from "../../../utils";
import { format } from "date-fns";
import { Fragment, useEffect, useRef, useState } from "react";
import {
  AdzapService,
  AgendamentoServicosService,
  MensagemPersonalizadaService,
  notification,
  PessoaService,
  SessionService,
} from "../../../services";
import { InputErros, value } from "../../../helpers";
import AgendamentoServicosValidator from "./middlewares/agendamento-servicos.validator";

const AddAgendamentoDialog = ({
  open,
  setOpen,
  dataSelecionada,
  agendamento,
  setAgendamento,
  setAgendamentosMesList,
  buscarAgendamentos,
  formatFilterDate,
  dadosEmpresa,
  setLoading,
}) => {
  const inputCliente = useRef();
  const inputPrestadorServico = useRef();
  const [clienteList, setClienteList] = useState([]);
  const [prestadorServicoList, setPrestadorServicoList] = useState([]);
  const [loadingAutoComplete, setLoadingAutoComplete] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const pessoaService = new PessoaService();
  const agendamentoServicosService = new AgendamentoServicosService();
  const mensagemPersonalizadaService = new MensagemPersonalizadaService();
  const adzapService = new AdzapService();
  const sessionService = new SessionService();
  const empresaUsuario = sessionService.getEmpresaAndUsuario();

  const tiposPessoa = [
    {
      tipoId: 1,
      setLista: (dados) => setClienteList(dados),
    },
    {
      tipoId: 5,
      setLista: (dados) => setPrestadorServicoList(dados),
    },
  ];

  function onCloseDialog() {
    setAgendamento({});
    setOpen(false);
  }

  useEffect(() => {
    if (open) {
      if (agendamento?.prestadorServicoId) {
        buscarPessoasPorTipo({ id: agendamento?.prestadorServicoId }, 5);
      }
    }
  }, [open]);

  const onSearchChangePessoas = async (event, inputRef, tiposPessoaId) => {
    if (event.key === "Enter" && inputRef.current === document.activeElement) {
      const eventValue = event.target?.value;
      let filtros = {
        nomeRazaoSocial: eventValue,
        apelidoFantasia: eventValue,
      };
      setLoadingAutoComplete(true);
      buscarPessoasPorTipo(filtros, tiposPessoaId);
    }
  };
  const buscarPessoasPorTipo = async (filtros, tiposPessoaId) => {
    const filtro = {
      ...filtros,
      tiposPessoaId,
      nonPaginated: true,
      ativado: true,
    };
    const tipoPessoaTemp = tiposPessoa.find(
      ({ tipoId }) => tipoId === tiposPessoaId,
    );
    const pessoas = await buscarPessoas(filtro);
    setLoadingAutoComplete(false);
    if (pessoas?.length) {
      tipoPessoaTemp?.setLista(pessoas);
    }
  };

  async function buscarPessoas(filtros) {
    const res = await pessoaService.getAll(filtros);
    if (!res.isAxiosError) {
      setLoadingAutoComplete(false);
      return res.data;
    } else {
      responseErros(res);
    }
    setLoadingAutoComplete(false);
    return [];
  }

  const onChangeAutocompleteCliente = (newValue) => {
    setAgendamento((prevState) => ({
      ...prevState,
      clienteId: newValue?.id,
      clienteNome: newValue?.nomeRazaoSocial,
      celular: newValue?.celular,
    }));
  };

  const adicionarAgendamentoMes = (agendamentos) => {
    const dataReferencia = format(
      formatDate.received(agendamento.periodo),
      "yyyy-MM-dd",
    );
    const periodo = agendamentos.find(({dia}) => dataReferencia === dia);
    if (periodo) {
      return agendamentos.map((item) => 
        item.data === dataReferencia ? { ...item, total: item.total + 1 } : item
      );
    } else {
      return [
        ...agendamentos,
        {
          dia: dataReferencia,
          total: 1,
        },
      ];
    }
  }

  const handleSubmit = async () => {
    setLoading(true);
    AgendamentoServicosValidator.validate(agendamento, { abortEarly: false })
      .then(async () => {
        let mensagemLembrete = null;
        if (
          (dadosEmpresa.whatsappSimples || dadosEmpresa.whatsappAvancado) && 
          dadosEmpresa.configuracoes?.lembreteAntecedenciaServico
        ) {
          mensagemLembrete = await enviarMensagemPorContexto(
            agendamento,
            "lembreteAgendamentoServico",
          );
        }
        const body = {
          ...agendamento,
          lembreteAntecedencia:
            dadosEmpresa.configuracoes?.lembreteAntecedenciaServico,
          mensagemLembrete,
        };
        if (agendamento?.id) {
          const response = await agendamentoServicosService.atualizar(
            agendamento?.id,
            body,
          );
          setLoading(false);
          if (!response.isAxiosError) {
            onCloseDialog();
            notification.alteracaoSucesso();
            const porIntervalo = formatFilterDate(agendamento.periodo);
            buscarAgendamentos({ porIntervalo });
          } else {
            responseErros(response);
          }
        } else {
          const retornoAlerta = await notification.confirmacao(
            "Confirmação",
            `Deseja confirmar o agendamento para ${format(
              formatDate.received(agendamento.periodo),
              "dd/MM/yyyy 'às' HH:mm?",
            )}`,
          );
          if (retornoAlerta.isConfirmed) {
            const response = await agendamentoServicosService.cadastrar(body);
            if (!response.isAxiosError) {
              notification.cadastroSucesso();
              await enviarConfirmacaoAgendamento(agendamento);
              setAgendamentosMesList((prev) => adicionarAgendamentoMes(prev));
              const porIntervalo = formatFilterDate(agendamento.periodo);
              buscarAgendamentos({ porIntervalo });
              if (!response.data?.mensagemEnviada) {
                notification.alertaGenericos("Lembrete não pode ser enviado. Favor verificar a conexão!");
              }
              onCloseDialog();
            } else { 
              responseErros(response);
            }
          }
        }
      })
      .catch((err) => {
        if (err.isAxiosError) {
          responseErros(err);
        } else {
          inputErros.set(err);
        }
      })
      .finally(() => setLoading(false));
  };

  async function enviarConfirmacaoAgendamento(agendamento) {
    const mensagem = await enviarMensagemPorContexto(
      agendamento,
      "agendamentoServico",
    );

    await adzapService.enviarMensagem({
      number: agendamento.celular,
      type: "text",
      text: mensagem,
    });
  }

  async function enviarMensagemPorContexto(agendamento, contexto) {
    const getPrimeiroNome = (nomeCompleto) => nomeCompleto?.split(" ")[0] || "";

    const texto = await mensagemPersonalizadaService.getMensagem({
      contexto,
      apelidoFantasia: empresaUsuario.empresa?.fantasia,
      nomeCompletoCliente: agendamento.clienteNome,
      primeiroNomeCliente: getPrimeiroNome(agendamento.clienteNome),
      nomeUsuario: empresaUsuario?.usuario?.nome,
      dataAgendamento: format(new Date(agendamento.periodo), "dd/MM/yyyy"),
      horaAgendamento: format(new Date(agendamento.periodo), "HH:mm"),
    });

    if (texto.isAxiosError) {
      throw texto;
    }

    return texto.data;
  }

  const handleChangeTextfield = (event) => {
    setAgendamento((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      PaperComponent="div"
      PaperProps={{
        style: {
          borderRadius: "0.7rem",
          background: "white",
        },
      }}
      open={open}
      onClose={() => onCloseDialog()}
    >
      <DialogContent>
        <h6 className="text-uppercase fw-bold pt-2 mx-3">ADICIONAR SERVIÇO</h6>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                id="clienteId"
                name="clienteId"
                options={clienteList}
                autoHighlight
                getOptionLabel={(option) => {
                  let codigoPessoa = option?.codigo
                    ? `${option.codigo} - `
                    : "";
                  let descricaoPessoa =
                    codigoPessoa + `${option.nomeRazaoSocial}`;
                  return option ? descricaoPessoa : "";
                }}
                loading={loadingAutoComplete}
                loadingText="Carregando"
                noOptionsText={
                  document.getElementById("clienteId")?.value.length >= 1 ? (
                    <Grid container spacing={1}>
                      <Grid item xs={12} fullWidth>
                        Nenhum cliente encontrado
                      </Grid>
                    </Grid>
                  ) : (
                    "Digite e pressione Enter"
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Buscar Cliente"}
                    variant={"outlined"}
                    onKeyDown={(e) => onSearchChangePessoas(e, inputCliente, 1)}
                    inputRef={inputCliente}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoComplete ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(_, newValue) => {
                  onChangeAutocompleteCliente(newValue);
                }}
                value={value.autoComplete(clienteList, agendamento?.clienteId)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="clienteNome"
                name="clienteNome"
                label="Cliente"
                variant="outlined"
                disabled={agendamento?.clienteId}
                onChange={handleChangeTextfield}
                value={agendamento?.clienteNome ?? ""}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                id="prestadorServicoId"
                name="prestadorServicoId"
                options={prestadorServicoList}
                autoHighlight
                getOptionLabel={(option) => {
                  let codigoPessoa = option?.codigo
                    ? `${option.codigo} - `
                    : "";
                  let descricaoPessoa =
                    codigoPessoa + `${option.nomeRazaoSocial}`;
                  return option ? descricaoPessoa : "";
                }}
                loading={loadingAutoComplete}
                loadingText="Carregando"
                noOptionsText={
                  document.getElementById("prestadorServicoId")?.value.length >=
                  1 ? (
                    <Grid container spacing={1}>
                      <Grid item xs={12} fullWidth>
                        Nenhum prestador encontrado
                      </Grid>
                    </Grid>
                  ) : (
                    "Digite e pressione Enter"
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={"Buscar Prestador de Serviço"}
                    variant={"outlined"}
                    onKeyDown={(e) =>
                      onSearchChangePessoas(e, inputPrestadorServico, 5)
                    }
                    inputRef={inputPrestadorServico}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          <Fragment>
                            {loadingAutoComplete ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </Fragment>
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(_, newValue) =>
                  setAgendamento((prev) => ({
                    ...prev,
                    prestadorServicoId: newValue.id,
                  }))
                }
                value={value.autoComplete(
                  prestadorServicoList,
                  agendamento?.prestadorServicoId,
                )}
              />
            </Grid>
            <Grid item xs={5}>
              <MaskedInput
                id="celular"
                label="Celular"
                name="celular"
                margin="none"
                mask="(99) 9 9999-9999"
                InputLabelProps={{ shrink: true }}
                onChange={handleChangeTextfield}
                value={agendamento?.celular ?? ""}
                InputProps={{
                  endAdornment: (
                    <IconButton disabled>
                      <i
                        className="ph-fill ph-device-mobile"
                        style={{
                          color: "#494C62",
                          fontSize: 20,
                        }}
                      ></i>
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <DatePicker
                id="data"
                name="data"
                label="Data"
                format="dd/MM/yyyy"
                disabled
                value={value.date(dataSelecionada)}
              />
            </Grid>
            <Grid item xs={3}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={ptBR}
              >
                <DesktopTimePicker
                  sx={{ width: "100%" }}
                  name="hora"
                  label="Hora"
                  onChange={(time) => {
                    if (!time || !dataSelecionada) return;
                    const date = new Date(dataSelecionada);
                    date.setHours(time.getHours());
                    date.setMinutes(time.getMinutes());
                    date.setSeconds(0);
                    date.setMilliseconds(0);
                    setAgendamento((prev) => ({
                      ...prev,
                      periodo:
                        date != "Invalid Date" ? date.toISOString() : null,
                    }));
                  }}
                  value={
                    agendamento?.periodo
                      ? formatDate.received(agendamento?.periodo)
                      : null
                  }
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <TextField
                id="observacao"
                name="observacao"
                label="Observação"
                variant="outlined"
                value={agendamento?.observacao ?? ""}
                onChange={handleChangeTextfield}
                InputProps={{ inputProps: { maxLength: 50 } }}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                color="padrao"
                onClick={() => onCloseDialog()}
                className="mt-3 rounded-3 fw-bolder"
                disableElevation
              >
                FECHAR
              </Button>
            </Grid>
            <Grid item xs={9}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => handleSubmit()}
                className="mt-3 rounded-3 fw-bolder"
                disableElevation
              >
                CONFIRMAR AGENDAMENTO
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </DialogContent>
    </Dialog>
  );
};

export default AddAgendamentoDialog;
